import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useCreateBundle } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CreateBundlePayload } from '~/services/AccommodationService';

import BundleLpcRatePlanIdsPicker from './components/BundleLpcRatePlanIdsPicker';
import BundlePropertyPicker, { PropertyOption } from './components/BundlePropertyPicker';
import { ALL_BOARD_CODES, ALL_BUNDLE_STATUSES, ALL_CANCELLATION_POLICIES } from './constants';

interface OccupancyRow {
  adults: number | '';
  children: number | '';
  infants: number | '';
}

interface FormState {
  name: string;
  status: NonNullable<CreateBundlePayload['status']>;
  properties: Array<PropertyOption>;
  los: number | '';
  leadDays: number | '';
  boardCodes: Array<CreateBundlePayload['rule']['boardCodes'][number]>;
  cancellationPolicies: Array<CreateBundlePayload['rule']['cancellationPolicies'][number]>;
  lpcRatePlanIds: Array<string>;
  allowAllLpcRatePlans: boolean;
  appliesToLpc: boolean;
  appliesToLpp: boolean;
  includedGuests: Array<OccupancyRow>;
}

const INITIAL_STATE: FormState = {
  name: '',
  status: 'draft',
  properties: [],
  los: 1,
  leadDays: 0,
  boardCodes: ['RO', 'BB', 'HB', 'FB', 'AI'],
  cancellationPolicies: ['RF', 'NRF'],
  lpcRatePlanIds: [],
  allowAllLpcRatePlans: false,
  appliesToLpc: false,
  appliesToLpp: false,
  includedGuests: [{ adults: 2, children: 2, infants: 1 }],
};

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function AccommodationBundleCreatePage() {
  const { push } = useAppNavigation();
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => INITIAL_STATE.includedGuests.map(allocateRowKey));
  const [conflictPropertyIds, setConflictPropertyIds] = useState<Array<string>>([]);

  const createBundleMutation = useCreateBundle();

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateOccupancyRow = useCallback((index: number, key: keyof OccupancyRow, value: number | '') => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  }, []);

  const addOccupancyRow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedGuests: [...prev.includedGuests, { adults: 0, children: 0, infants: 0 }],
    }));
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeOccupancyRow = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.filter((_, i) => i !== index),
    }));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setConflictPropertyIds([]);

      const propertyIds = state.properties.map((p) => p.id);

      const payload: CreateBundlePayload = {
        name: state.name,
        status: state.status,
        propertyIds,
        rule: {
          los: typeof state.los === 'number' ? state.los : 0,
          leadDays: typeof state.leadDays === 'number' ? state.leadDays : 0,
          boardCodes: state.boardCodes,
          cancellationPolicies: state.cancellationPolicies,
          lpcRatePlanIds: state.lpcRatePlanIds,
          allowAllLpcRatePlans: state.allowAllLpcRatePlans,
          appliesToLpc: state.appliesToLpc,
          appliesToLpp: state.appliesToLpp,
          includedGuests: state.includedGuests.map((row) => ({
            adults: typeof row.adults === 'number' ? row.adults : 0,
            children: typeof row.children === 'number' ? row.children : 0,
            infants: typeof row.infants === 'number' ? row.infants : 0,
          })),
          perOccupant: false,
          perNight: false,
        },
      };

      try {
        const result = await createBundleMutation.mutateAsync({
          payload,
          errorBodyCallback: (body) => {
            const conflicts =
              (body as { result?: { conflictingPropertyIds?: Array<string> } } | undefined)?.result
                ?.conflictingPropertyIds ?? [];
            if (conflicts.length > 0) {
              setConflictPropertyIds(conflicts);
            }
          },
        });
        push(`/accommodation/bundles/${result.bundleId}`);
      } catch {
        // Non-409 errors are surfaced via the hook's snackbar; 409 conflicts render inline above.
      }
    },
    [state, createBundleMutation, push],
  );

  const isSubmitting = createBundleMutation.isPending;
  const canSubmit =
    !!state.name.trim() &&
    state.properties.length > 0 &&
    state.boardCodes.length > 0 &&
    state.cancellationPolicies.length > 0;

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>Create Bundle</title>
      </Helmet>
      <PageHeader title="Create Bundle" backButton="/accommodation/properties#bundles" />

      <Box component="form" onSubmit={handleSubmit} mt={2}>
        {conflictPropertyIds.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            One or more property IDs are already attached to another bundle: {conflictPropertyIds.join(', ')}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Bundle Name"
            value={state.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={state.status}
              onChange={(e) => handleChange('status', e.target.value as NonNullable<CreateBundlePayload['status']>)}
            >
              {ALL_BUNDLE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <BundlePropertyPicker value={state.properties} onChange={(next) => handleChange('properties', next)} />

          <Box>
            <Box mb={1} fontWeight={500}>
              Hotel Rule
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Min length of stay (nights)"
                  type="number"
                  value={state.los}
                  onChange={(e) => handleChange('los', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Lead days"
                  type="number"
                  value={state.leadDays}
                  onChange={(e) => handleChange('leadDays', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Board Codes</InputLabel>
                  <Select
                    multiple
                    label="Board Codes"
                    value={state.boardCodes}
                    onChange={(e) => handleChange('boardCodes', e.target.value as FormState['boardCodes'])}
                  >
                    {ALL_BOARD_CODES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Cancellation Policies</InputLabel>
                  <Select
                    multiple
                    label="Cancellation Policies"
                    value={state.cancellationPolicies}
                    onChange={(e) =>
                      handleChange('cancellationPolicies', e.target.value as FormState['cancellationPolicies'])
                    }
                  >
                    {ALL_CANCELLATION_POLICIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={0.5}>
                  Product types
                </Typography>
                <Box display="flex" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.appliesToLpc}
                        onChange={(e) => handleChange('appliesToLpc', e.target.checked)}
                      />
                    }
                    label="Apply to LPC"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.appliesToLpp}
                        onChange={(e) => handleChange('appliesToLpp', e.target.checked)}
                      />
                    }
                    label="Apply to LPP"
                  />
                </Box>
                <Typography
                  variant="caption"
                  color={!state.appliesToLpc && !state.appliesToLpp ? 'warning.main' : 'text.secondary'}
                  display="block"
                >
                  {!state.appliesToLpc && !state.appliesToLpp
                    ? 'Both unticked — this bundle will not attach to any rate until you tick LPC and/or LPP.'
                    : 'Bundle only attaches to rate types that are ticked.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.allowAllLpcRatePlans}
                      onChange={(e) => handleChange('allowAllLpcRatePlans', e.target.checked)}
                    />
                  }
                  label="Allow all LPC rate plans (bypass allowlist)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  When ticked, the bundle attaches to every matching LPC rate regardless of the allowlist below.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <BundleLpcRatePlanIdsPicker
                  value={state.lpcRatePlanIds}
                  onChange={(next) => handleChange('lpcRatePlanIds', next)}
                  propertyIds={state.properties.map((p) => p.id)}
                  disabled={state.allowAllLpcRatePlans}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>
                  Occupancy maximums
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  A rate must fit within at least one of these maximums to be eligible. Add multiple rows to support
                  different occupancy combinations (e.g. "4 adults solo" and "2 adults + 2 children + 1 infant").
                </Typography>
                <Stack spacing={2}>
                  {state.includedGuests.map((row, index) => (
                    <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Maximum #{index + 1}</Typography>
                        <IconButton
                          onClick={() => removeOccupancyRow(index)}
                          size="small"
                          aria-label="Remove occupancy maximum"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Adults"
                            type="number"
                            value={row.adults}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'adults', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Children"
                            type="number"
                            value={row.children}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'children', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Infants"
                            type="number"
                            value={row.infants}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'infants', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
                <Box mt={2}>
                  <Button onClick={addOccupancyRow} variant="outlined">
                    + Add maximum
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => push('/accommodation/properties#bundles')}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Bundle'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}

export default AccommodationBundleCreatePage;
